import axios from 'axios';

const api = axios.create({
  baseURL: (process.env.PUBLIC_URL || '') + '/api',
  timeout: 30000,
});

// Attach JWT token to every request
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('identium_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// ─── Timezone fix ──────────────────────────────────────────────────────────
// SQLite returns CURRENT_TIMESTAMP as `YYYY-MM-DD HH:MM:SS` with NO timezone
// marker — but the value is UTC. When the frontend does
//   new Date("2026-05-03 20:31:45").toLocaleString()
// browsers parse the string as LOCAL time (since there's no Z), so what
// should display as "2:01 AM IST next day" appears as "8:31 PM IST today" —
// off by exactly the local UTC offset (5h 30m for IST).
//
// We fix it on every API response, but ONLY rewrite values whose key
// name suggests a timestamp (ends in `_at`, `_date`, `created`, `updated`,
// `_time`, `timestamp`, `last_*`, etc.) — earlier this walked every string
// in the body and would silently rewrite a `notes` or `barcode` value that
// happened to match the regex. Also bails out on huge payloads to keep the
// activity log / 11k-row asset list snappy.
const SQLITE_DT = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}(\.\d+)?$/;
const TIMESTAMP_KEY = /(_at|_date|_time|timestamp|^created$|^updated$|^last[_A-Z])/i;
const toIsoUtc = (s) => s.replace(' ', 'T') + 'Z';
const fixDates = (val, keyHint = '') => {
  if (val == null) return val;
  if (typeof val === 'string') {
    // Only rewrite when the parent key looks like a timestamp field. Without
    // this gate, a notes field whose user-typed value happened to match
    // YYYY-MM-DD HH:MM:SS got mangled to ISO-UTC every time it round-tripped.
    if (!keyHint || !TIMESTAMP_KEY.test(keyHint)) return val;
    return SQLITE_DT.test(val) ? toIsoUtc(val) : val;
  }
  if (Array.isArray(val)) {
    for (let i = 0; i < val.length; i++) val[i] = fixDates(val[i], keyHint);
    return val;
  }
  if (typeof val === 'object') {
    for (const k of Object.keys(val)) val[k] = fixDates(val[k], k);
    return val;
  }
  return val;
};

// Handle responses: normalize timestamps + handle 401s globally.
api.interceptors.response.use(
  (response) => {
    try { fixDates(response.data); } catch {}
    return response;
  },
  (error) => {
    if (!(error.response && error.response.status === 401)) {
      return Promise.reject(error);
    }

    // Two distinct 401 cases — only ONE should auto-redirect.
    //
    //   1. The user just submitted wrong creds at the login form.
    //      The login endpoint returns 401 to mean "those creds are
    //      wrong"; the form's catch block needs to surface that as an
    //      inline error. If we redirect here, the page reloads to a
    //      generic `/login` (losing any per-company slug like
    //      /testcompany/login) and the error message never reaches
    //      the user. Skip the redirect — let the caller handle it.
    //
    //   2. The user has an expired or revoked session token. ANY
    //      other endpoint returning 401 means we're stale; clear
    //      local state and bounce back to the login screen.
    //      For per-company logins (URL `/<slug>/login`), bounce back
    //      to that same per-company URL — landing on bare `/login`
    //      after a session timeout would force them to type the
    //      company slug again.
    const url = error.config?.url || '';
    const isLoginAttempt = url.includes('/auth/login');
    if (isLoginAttempt) {
      return Promise.reject(error);
    }

    localStorage.removeItem('identium_token');
    localStorage.removeItem('identium_user');

    // Reconstruct the per-company login URL when we know the slug.
    // First check the current path (in case the user was viewing a
    // page under /<slug>/something), then fall back to the saved
    // last-company in localStorage.
    const BASE = process.env.PUBLIC_URL || '';
    let path = window.location.pathname;
    if (BASE && path.startsWith(BASE)) path = path.slice(BASE.length) || '/';
    const slugFromPath = path.match(/^\/([a-z0-9-]+)\//)?.[1];
    const slugFromStorage = localStorage.getItem('identium_last_company');
    const RESERVED = new Set([
      'login', 'dashboard', 'companies', 'settings', 'system-health',
      'asset-registration', 'all-assets', 'assets', 'location-master',
      'locations', 'asset-movement', 'asset-audit', 'audit-report',
      'asset-search', 'raw-inventory', 'handheld-simulator',
      'handheld-guide', 'reader-manager', 'user-management', 'reports',
      'analytics', 'activity-logs', 'user-guide', 'host', 'c',
      'downloads', 'settings', 'companies', 'system-health',
    ]);
    const slug = (slugFromPath && !RESERVED.has(slugFromPath))
      ? slugFromPath
      : (slugFromStorage && /^[a-z0-9-]+$/.test(slugFromStorage)
          ? slugFromStorage
          : null);

    window.location.href = slug ? `${BASE}/${slug}/login` : `${BASE}/login`;
    return Promise.reject(error);
  }
);

export default api;
